// Speed / distance tables (§2.2, §2.3). Exact integer accumulation, computed
// once lazily so every package sees byte-identical numbers.

import {
  MAX_FRAMES,
  PLAYER_START_X,
  SPEED_END,
  SPEED_RAMP_FRAMES,
  SPEED_START,
  FQ_PER_METER,
} from './constants';

/** speed(f) = SPEED_START + ((SPEED_END - SPEED_START) * min(f, ramp) / ramp | 0) */
export function speedAtFrame(f: number): number {
  let c = f | 0;
  if (c < 0) c = 0;
  else if (c > SPEED_RAMP_FRAMES) c = SPEED_RAMP_FRAMES;
  return (SPEED_START + ((Math.imul(SPEED_END - SPEED_START, c) / SPEED_RAMP_FRAMES) | 0)) | 0;
}

let table: Int32Array | null = null;

function ensureTable(): Int32Array {
  let t = table;
  if (t === null) {
    t = new Int32Array(MAX_FRAMES + 1); // f = 0 .. 3600 inclusive
    let acc = 0;
    for (let f = 0; f < MAX_FRAMES; f++) {
      acc = (acc + speedAtFrame(f)) | 0;
      t[f + 1] = acc;
    }
    table = t;
  }
  return t;
}

/**
 * Distance travelled after `f` simulated frames, FQ.
 * distanceAtFrame(0) = 0; distanceAtFrame(n) = sum of speed(0..n-1).
 * Clamped to [0, MAX_FRAMES].
 */
export function distanceAtFrame(f: number): number {
  const t = ensureTable();
  let i = f | 0;
  if (i <= 0) return 0;
  if (i > MAX_FRAMES) i = MAX_FRAMES;
  return t[i]!;
}

/** Display meters. 1 grid = 1024 FQ = 2 m, so 1 m = 512 FQ. */
export function metersFromFQ(x: number): number {
  return Math.floor(x / FQ_PER_METER);
}

/**
 * First frame f with distanceAtFrame(f) >= d (binary search over the table).
 * Returns MAX_FRAMES when the distance is never reached.
 */
export function frameAtDistance(d: number): number {
  const t = ensureTable();
  const target = d | 0;
  if (target <= 0) return 0;
  if (target > t[MAX_FRAMES]!) return MAX_FRAMES;
  let lo = 0;
  let hi = MAX_FRAMES;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid]! >= target) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

/** Frame at which the player's reference x reaches an absolute track x. */
export function frameAtTrackX(x: number): number {
  return frameAtDistance((x - PLAYER_START_X) | 0);
}

/** Absolute track x of the player's reference point at frame f. */
export function trackXAtFrame(f: number): number {
  return (PLAYER_START_X + distanceAtFrame(f)) | 0;
}
